import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { reduxForm, Field } from 'redux-form';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import ORTypography from 'components/ORTypography/ORTypography';
import { required, validateCidr, maxLength } from 'services/validationMsgs';
import { normalizeCidr } from 'services/normalizer';

const maxLength128 = maxLength(128);

const propTypes = {
  myip: PropTypes.string,
  availableAclKinds: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedService: PropTypes.string.isRequired,
  selectedCidr: PropTypes.string.isRequired,
  change: PropTypes.func.isRequired,
};

const defaultProps = {
  myip: '',
};

const useStyles = makeStyles((theme) => ({
  howText: {
    fontSize: '.8571rem',
    color: theme.customColors.mediumGrey,
  },
  stepThreeForm: {
    display: 'flex',
    flexDirection: 'column',
  },
  stepThreeField: {
    marginTop: theme.spacing(2),
  },
  infoText: {
    display: 'flex',
    marginTop: theme.spacing(2),
    fontSize: '.8571rem',
    color: theme.customColors.mediumGrey,
  },
  infoIcon: {
    fontSize: '1.14285rem',
    marginTop: theme.spacing(0.25),
    marginRight: theme.spacing(0.5),
  },
}));

function UnstyledStepThree({ myip, availableAclKinds, selectedService, selectedCidr, change }) {
  React.useEffect(() => {
    if (!selectedCidr) {
      change('cidr', myip);
    }
  }, [change, myip, selectedCidr]);

  React.useEffect(() => {
    // For the create flow only, we're adding one firewall IP for all
    // ACL kinds for a service
    change(
      'cidrKinds',
      availableAclKinds.map((aclKind) => aclKind.kind),
    );
  }, [availableAclKinds, change]);

  const classes = useStyles();

  return (
    <React.Fragment>
      <ORTypography className={classes.howText}>
        <strong>How it works:</strong>
        <br />
        In order to secure your instance, all access is blocked by default. In order to connect to
        your instance, you will need to authorize an IP.
      </ORTypography>

      <form className={classes.stepThreeForm}>
        <Field
          className={classes.stepThreeField}
          name="cidr"
          label="Your current IP"
          variant="outlined"
          validate={[validateCidr, required]}
          normalize={normalizeCidr}
          component={WrappedTextField}
        />
        <Field
          className={classes.stepThreeField}
          name="cidrName"
          label="IP Name"
          variant="outlined"
          validate={[maxLength128, required]}
          component={WrappedTextField}
        />
      </form>

      {selectedService === 'Elasticsearch' && (
        <ORTypography className={classes.infoText}>
          <InfoOutlinedIcon className={classes.infoIcon} />
          <em>
            You can add, remove, or delete IPs after creating your Instance. For more in-depth
            information regarding IPs,{' '}
            <a
              href="https://docs.objectrocket.com/elastic_getting_started.html#"
              target="_blank"
              rel="noopener noreferrer"
            >
              please&nbsp;see&nbsp;our&nbsp;docs
            </a>
            .
          </em>
        </ORTypography>
      )}
    </React.Fragment>
  );
}

UnstyledStepThree.propTypes = propTypes;
UnstyledStepThree.defaultProps = defaultProps;

export default reduxForm({
  form: 'create',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(UnstyledStepThree);
